import { Badge } from 'components/bgp-overview/Badge';
import React from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { useQuery } from 'react-query';
import { ASN, Prefix } from 'ripe/common';
import { getPrefixVisibility, PrefixVisibility } from 'upx-tools/prefix-visibility';
import { BadgeColor } from '../Badge';
import { BoxPanel } from '../BoxPanel';
import { HorizontalDashedConnector, VerticalDashedConnector } from '../DashedConnectors/DashedConnectors';
import { Divider } from '../Divider';
import { PrefixCard } from '../PrefixCard';

export interface PrefixRowProps {
  prefix: Prefix;
  originAsn: ASN;
  upstreamAsns: ASN[];
}

export const PrefixRow: React.FC<PrefixRowProps> = ({ prefix, originAsn, upstreamAsns }: PrefixRowProps) => {
  const { data: prefixVisibility } = useQuery(['prefix-visibility', prefix], () => getPrefixVisibility(prefix), {
    refetchInterval: 3000,
  });

  if (!prefixVisibility) {
    return null;
  }

  const statusColor = getPrefixStatusColor(prefixVisibility, originAsn);

  return (
    <div className="w-full flex">
      <PrefixCard prefix={prefixVisibility.prefix} badgeColor={statusColor} />
      <div className="w-4 flex flex-none items-center mt-5">
        <HorizontalDashedConnector color={statusColor} />
      </div>

      <div className="flex-1 h-full max-w-max">
        <div className="h-5 w-full flex justify-around">
          {upstreamAsns.map((asn) => (
            <React.Fragment key={asn}>{upstreamAsns.length - 1 ? <VerticalDashedConnector /> : null}</React.Fragment>
          ))}
        </div>

        <BoxPanel className="flex-nowrap w-full h-full justify-around">
          {upstreamAsns.map((asn, index) => {
            const upstreamRouteCount = prefixVisibility.routeCountByUpstreamAsn[asn] ?? 0;
            const upstreamVisibility =
              0 === upstreamRouteCount
                ? 0
                : Math.round((upstreamRouteCount * 100) / prefixVisibility.transitedRouteCount);

            const activeColor = '#56CCF2';
            const color = upstreamVisibility <= 0 ? 'gray' : activeColor;
            const baseColor = upstreamVisibility <= 0 ? '#c7c9d2' : activeColor;
            const colorWithAlpha = upstreamVisibility <= 0 ? '#c7c9d280' : '#24b8e880';

            return (
              <div key={index} className="flex flex-row items-center w-40 h-full">
                {0 === index && <Badge badgeColor={statusColor} />}
                {0 !== index && <Divider />}
                <div className="p-2 w-full flex justify-center text-center flex-wrap break-words">
                  <div className="w-12">
                    <CircularProgressbar
                      value={upstreamVisibility}
                      text={`${upstreamVisibility}%`}
                      styles={{
                        text: {
                          fill: `${color}`,
                          fontSize: 24,
                          fontWeight: 600,
                        },
                        path: {
                          stroke: `${baseColor}`,
                        },
                        trail: {
                          stroke: `${colorWithAlpha}`,
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </BoxPanel>
      </div>
    </div>
  );
};

const getPrefixStatusColor = (prefixVisibility: PrefixVisibility, originAsn: ASN): BadgeColor => {
  const validRouteCount = prefixVisibility.routeCountByOriginAsn[originAsn] ?? 0;

  if (0 === validRouteCount) {
    return 'default';
  }

  if (validRouteCount < 2) {
    return 'warning';
  }

  return 'success';
};
